import { FC, useRef, useState, KeyboardEvent, ClipboardEvent, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';

interface Props {
  otpLength?: number;
  value?: string;
  onComplete?: (otp: string) => void;
  onChange?: (otp: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  invalid?: boolean;
}

export const OTPField: FC<Props> = ({
  otpLength = 6,
  value,
  onComplete,
  onChange,
  autoFocus = true,
  disabled = false,
  invalid = false,
}) => {
  const { t } = useTranslation('digital-forms-experience');
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [otp, setOtp] = useState<string[]>(Array(otpLength).fill(''));
  const lastUsedValueFieldRef = useRef<string>('');

  // Sync internal state with external value prop only when full OTP is provided and value changes
  useEffect(() => {
    if (value && value.length === otpLength && lastUsedValueFieldRef.current !== value) {
      // don't autofill if value is not number
      if (!/^\d+$/.test(value)) {
        return;
      }
      setOtp([...value]);
      lastUsedValueFieldRef.current = value;
    }
  }, [otpLength, value]);

  // Auto focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus, disabled]);

  // Call onComplete when OTP is filled
  useEffect(() => {
    const otpString = otp.join('');
    onChange?.(otpString);

    if (otpString.length === otpLength && !otpString.includes('')) {
      onComplete?.(otpString);
    }
  }, [otp, otpLength, onComplete, onChange]);

  const focusInput = (index: number) => inputRefs.current[index]?.focus();
  const focusNextInput = (index: number) => {
    if (index + 1 < otpLength) {
      focusInput(index + 1);
    }
  };
  const focusPrevInput = (index: number) => {
    if (index - 1 >= 0) {
      focusInput(index - 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (disabled) return;

    const { key } = e;

    // Handle numeric input - this catches same-value entries that onChange misses
    if (/^[0-9]$/.test(key)) {
      e.preventDefault();

      const newOtp = [...otp];
      newOtp[index] = key;
      setOtp(newOtp);

      // Always move to next input when a digit is entered
      if (index < otpLength - 1) {
        focusNextInput(index);
      }
      return;
    }

    // Handle backspace
    if (key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];

      if (otp[index]) {
        // Clear current input
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        // Move to previous input and clear it
        newOtp[index - 1] = '';
        setOtp(newOtp);
        focusPrevInput(index);
      }
    }

    // Handle Delete key
    else if (key === 'Delete') {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
    }

    // Handle Arrow keys
    else if (key === 'ArrowLeft') {
      e.preventDefault();
      focusPrevInput(index);
    } else if (key === 'ArrowRight') {
      e.preventDefault();
      focusNextInput(index);
    }

    // Handle Home key
    else if (key === 'Home') {
      e.preventDefault();
      focusInput(0);
    }

    // Handle End key
    else if (key === 'End') {
      e.preventDefault();
      focusInput(otpLength - 1);
    }

    // Handle Enter - focus next empty input or complete if full
    else if (key === 'Enter') {
      e.preventDefault();
      const nextEmptyIndex = otp.findIndex((digit) => !digit);
      if (nextEmptyIndex !== -1) {
        focusInput(nextEmptyIndex);
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '');

    if (pastedData.length > 0) {
      const newOtp = [...otp];
      const currentIndex = parseInt(e.currentTarget.dataset.index || '0');

      for (let i = 0; i < Math.min(pastedData.length, otpLength - currentIndex); i++) {
        newOtp[currentIndex + i] = pastedData[i];
      }

      setOtp(newOtp);

      // Focus next empty input or last input
      const nextIndex = Math.min(currentIndex + pastedData.length, otpLength - 1);
      focusInput(nextIndex);
    }
  };

  // Select all text on focus for easier replacement
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const inputItemIndexArray = useMemo(() => Array.from({ length: otpLength }, (_, index) => index), [otpLength]);

  return (
    <div css={containerStyle}>
      {inputItemIndexArray.map((index) => (
        <input
          key={index}
          type='text'
          inputMode='numeric'
          pattern='[0-9]*'
          maxLength={1}
          value={otp[index]}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={handleFocus}
          ref={(ref) => {
            if (ref) inputRefs.current[index] = ref;
          }}
          data-index={index}
          disabled={disabled}
          autoComplete='one-time-code'
          css={[inputStyle, invalid && invalidInputStyle, disabled && disabledInputStyle]}
          aria-label={t('OTP digit {{index}} of {{length}}', { index: index + 1, length: otpLength })}
        />
      ))}
    </div>
  );
};

const containerStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
});

const inputStyle = css({
  borderWidth: '0 0 2px 0',
  borderColor: theme.colors.primary50,
  width: 35,
  height: 40,
  textAlign: 'center',
  fontSize: theme.fontSize(24),
  fontWeight: theme.font.weight.bold,
  outline: 'none',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: theme.colors.white,
  color: theme.colors.neutral90,

  '&:focus': {
    transform: 'scale(1.05)',
  },

  '&:hover:not(:disabled)': {
    borderColor: theme.colors.primary40,
  },

  // Remove spinner arrows for number inputs
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '&[type=number]': {
    '-moz-appearance': 'textfield',
  },
});

const invalidInputStyle = css({ borderColor: theme.colors.critical40 });

const disabledInputStyle = css({
  borderColor: theme.colors.neutral20,
  color: theme.colors.neutral40,
  cursor: 'not-allowed',
});
